import React, { useState, useEffect, useContext } from 'react';
import tabStyles from './EnquiriesTab.module.css';
import { isMobile } from '../../../../Constants';
import { DataGhost } from '../../../Planning/components/EmptyState';
import Pagination from 'react-js-pagination';
import ClarificationThread from './ClarificationThread';
import { useClarificationUnread } from './useClarificationUnread';
import { useEnquiryClarificationUnread } from './useEnquiryClarificationUnread';
import Modal from '../../../Shared/PopupModal/PopupModal';
import panelStyles from './ClarificationsPanel.module.css';
import historyStyles from './HistoryTimeline.module.css';
import { MyCompanyProfileContext } from '../../../../MyCompanyProfileContext';
import { useMixPanelForTracking } from '../../../../utils/TrackMixPanel';

/* ─── Module-level helpers ─── */
const formatRate = (value) => {
    if (value === null || value === undefined || value === '') return 'NA';
    const num = Number(String(value).replace(/,/g, ''));
    return Number.isFinite(num) ? num.toLocaleString('en-IN') : String(value);
};

const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    // Bare "YYYY-MM-DD HH:mm:ss[.SSS]" strings (no timezone marker) are UTC on
    // the wire but get parsed as local time by `new Date()` — treat them as
    // UTC explicitly (ISO strings with Z/offset are unaffected by this check).
    const isBareUtc = typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(dateStr) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr);
    const normalized = isBareUtc ? `${dateStr.replace(' ', 'T')}Z` : dateStr;
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
        timeZone: 'Asia/Kolkata',
    });
};

const getTenureLabel = (tenure) => {
    if (!tenure) return null;
    if (typeof tenure === 'string') return tenure;
    if (typeof tenure === 'object') return tenure.label || tenure.value || null;
    return null;
};
const groupApprovalMetricsByLevel = (approvalMetrics) => {
    if (!Array.isArray(approvalMetrics)) return [];
    const groups = {};
    approvalMetrics.forEach((m) => {
        const level = m.level ?? 0;
        if (!groups[level]) groups[level] = [];
        groups[level].push(m);
    });
    return Object.keys(groups)
        .map(Number)
        .sort((a, b) => a - b)
        .map((level) => ({ level, approvers: groups[level] }));
};

/**
 * Build chronological history events from transporter_list[].rate_list[].
 * The API has no separate `history` field — we derive it from rate events.
 */
const buildHistory = (item) => {
    const events = [];
    const transporterList = item.transporter_list || item.transporter_rate_list || [];

    const enquiryCreatedTs =
        item.submission_date?.$date || item.submission_date ||
        item.enquiry_timestamp?.$date || item.enquiry_timestamp ||
        item.timestamp?.$date ||
        item.lane_details?.submission_date?.$date || item.lane_details?.submission_date ||
        null;

    if (enquiryCreatedTs) {
        events.push({
            ts: enquiryCreatedTs,
            text: 'Enquiry created',
            remark: null,
            color: '#7c3aed',
            icon: 'fa-flag',
        });
    }

    // Build rank map: L1 = lowest submitted rate
    const ranked = [...transporterList]
        .filter((t) => t.rate !== undefined && t.rate !== null)
        .sort((a, b) => parseFloat(a.rate) - parseFloat(b.rate));
    const rankMap = {};
    ranked.forEach((t, i) => { rankMap[t.transporter_id] = `L${i + 1}`; });

    for (const t of transporterList) {
        const rank = rankMap[t.transporter_id] || '';
        const tName = t.transporter_name || 'Transporter';

        for (const r of (t.rate_list || [])) {
            const rateStr = r.rate !== undefined && r.rate !== null ? `₹${formatRate(r.rate)}` : '';

            if (r.rate_by === 'transporter') {
                events.push({
                    ts: r.submission_date?.$date || null,
                    text: `${tName} submitted a quote of ${rateStr}`,
                    remark: null,
                    color: '#b0b8c8',
                });
            } else if (r.bid_status === 'accepted' && r.accepted_at) {
                events.push({
                    ts: r.accepted_at?.$date || r.accepted_at || null,
                    text: `${r.accepted_by || 'Unknown'} accepted ${rank} ${tName} at ${rateStr}`,
                    remark: r.remark || null,
                    color: '#393185',
                });
            } else if (r.bid_status === 'approved') {
                events.push({
                    ts: r.approved_at?.$date || r.approved_at || null,
                    text: `${r.approved_by || 'Unknown'} approved ${rank} ${tName} at ${rateStr}`,
                    remark: r.remark || null,
                    color: '#27ae60',
                });
            } else if (r.bid_status === 'rejected') {
                events.push({
                    ts: r.rejected_at?.$date || r.rejected_at || null,
                    text: `${r.rejected_by || 'Unknown'} rejected ${rank} ${tName} at ${rateStr}`,
                    remark: null,
                    color: '#e74c3c',
                });
            }
        }
    }

    events.sort((a, b) => {
        if (!a.ts && !b.ts) return 0;
        if (!a.ts) return -1;
        if (!b.ts) return 1;
        return new Date(a.ts) - new Date(b.ts);
    });

    return events;
};

/* ─── MessageModalButton: opens chat thread in a Popup Modal ─── */
const MessageModalButton = ({
    enquiryId,
    transporters = [],
    currentUserLevel,
    currentUserEmail,
    currentUserName,
    clarificationStatus,
    initialUnreadCount = 0,
}) => {
    const unreadCount = initialUnreadCount;
    const refresh = () => {};

    const [selectedTarget, setSelectedTarget] = useState(null);
    const [hasOpened, setHasOpened] = useState(false);

    useEffect(() => {
        if (hasOpened && selectedTarget === null && transporters.length > 0) {
            setSelectedTarget(transporters[0].transporter_id);
        }
    }, [hasOpened, selectedTarget, transporters]);

    const hasUnreadActivity = unreadCount > 0 || clarificationStatus === 'clarification_needed';

    const {
        unreadByTransporter: clarificationUnreadByTransporter,
        refresh: refreshClarificationUnread,
    } = useEnquiryClarificationUnread({ enquiryId, currentUserEmail });

    useEffect(() => {
        if (hasOpened && hasUnreadActivity) refreshClarificationUnread();
    }, [hasOpened, hasUnreadActivity, refreshClarificationUnread]);

    useEffect(() => {
        if (hasOpened && hasUnreadActivity) {
            const timeout = setTimeout(refreshClarificationUnread, 1500);
            return () => clearTimeout(timeout);
        }
    }, [hasOpened, hasUnreadActivity, selectedTarget, refreshClarificationUnread]);

    const needsClarification = clarificationStatus === 'clarification_needed';
    const selectedTransporter = transporters.find((t) => t.transporter_id === selectedTarget) || null;

    return (
        <Modal
            buttonLabel={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <i className="fa fa-comment-o" style={{ fontSize: '14px', color: '#393185' }} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#393185', textDecoration: 'underline' }}>Send Clarrification</span>
                    {unreadCount > 0 ? (
                        <span style={{
                            backgroundColor: '#e74c3c', color: '#fff', borderRadius: '50%',
                            width: '18px', height: '18px', display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: '11px', fontWeight: 700,
                        }}>
                            {unreadCount}
                        </span>
                    ) : (
                        needsClarification && (
                            <span style={{
                                backgroundColor: '#e67e22', color: '#fff', borderRadius: '50%',
                                width: '18px', height: '18px', display: 'inline-flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: '11px', fontWeight: 700,
                            }}>
                                !
                            </span>
                        )
                    )}
                </span>
            }
            buttonClass=""
            style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', marginTop: '4px',
            }}
            disabled={transporters.length === 0}
            content={({ onClose }) =>
                transporters.length >= 1 ? (
                    <div className={panelStyles.wrapper}>
                        <div className={panelStyles.sidebar}>
                            {transporters.map((t) => {
                                const unread = clarificationUnreadByTransporter[t.transporter_id] || 0;
                                return (
                                    <div
                                        key={t.transporter_id}
                                        onClick={() => setSelectedTarget(t.transporter_id)}
                                        className={`${panelStyles.sidebarRow} ${selectedTarget === t.transporter_id ? panelStyles.sidebarRowActive : ''
                                            }`}
                                        title={t.transporter_name}
                                    >
                                        <span className={panelStyles.sidebarRowLabel}>
                                            {t.transporter_name && t.transporter_name.length < 28
                                                ? t.transporter_name
                                                : `${(t.transporter_name || '').substring(0, 28)}...`}
                                        </span>
                                        {unread > 0 && <span className={panelStyles.unreadBadge}>{unread}</span>}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={panelStyles.main}>
                            {selectedTransporter && (
                                <ClarificationThread
                                    key={selectedTransporter.transporter_id}
                                    enquiryId={enquiryId}
                                    forTransporterId={selectedTransporter.transporter_id}
                                    currentUserLevel={currentUserLevel}
                                    currentUserEmail={currentUserEmail}
                                    currentUserName={currentUserName}
                                    onClose={onClose}
                                />
                            )}
                        </div>
                    </div>
                ) : null
            }
            title={selectedTransporter ? `Clarification: ${selectedTransporter.transporter_name}` : "Clarification"}
            id={`clarification-${enquiryId}`}
            size="lg"
            screenName="LoRRI Spot"
            moduleName="My Enquiries"
            onOpen={() => setHasOpened(true)}
            onClose={() => {
                refresh();
                return true;
            }}
        />
    );
};

/* ═══════════════════════════════════════════════════════════════════ */

const EnquiriesTab = ({
    approvalList = [],
    payload = [],
    setPayload,
    changeHandler,
    getOnChange,
    skipActive,
    totalCount,
    show,
    ApprovalHandler,
    enquiryTransition,
    currentUserLevel = 0,
    currentUserEmail,
    currentUserName,
    selectedTab,
}) => {
    const mobile = isMobile();
    const selectedPayload = Array.isArray(payload) ? payload : [];
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [rowRemarks, setRowRemarks] = useState({});
    const [expandedApprovalChains, setExpandedApprovalChains] = useState(new Set());

    const toggleApprovalChain = (enquiryId) => {
        setExpandedApprovalChains((prev) => {
            const next = new Set(prev);
            if (next.has(enquiryId)) next.delete(enquiryId);
            else next.add(enquiryId);
            return next;
        });
    };

    const { companySettingData } = useContext(MyCompanyProfileContext);
    const isMandatoryApprovalRemark = companySettingData?.enquiry?.mandatory_approval_remark === true;
    const { TrackMixPanel } = useMixPanelForTracking(
        { email: currentUserEmail, user_name: currentUserName },
        companySettingData
    );

    const toggleExpand = (enquiryId, item) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            const willExpand = !next.has(enquiryId);
            if (willExpand) next.add(enquiryId);
            else next.delete(enquiryId);

            TrackMixPanel({
                ModuleName: 'lorri spot',
                ScreenName: 'my enquiries - enquiry pending for approval',
                Data: `truck enquiry id: ${enquiryId}, adhoc id: ${item?.display_adhoc_id || ''}`,
                Definition: `user ${willExpand ? 'expanded' : 'collapsed'} enquiry row`,
            });

            return next;
        });
    };

    const getLastRate = (rateList) => {
        if (!Array.isArray(rateList) || rateList.length === 0) return null;
        return rateList[rateList.length - 1];
    };

    const isEnquirySelected = (enquiryId) =>
        selectedPayload.some((item) => item.truck_enquiry_id === enquiryId);

    const isTransporterSelected = (enquiryId, transporterId) =>
        selectedPayload.some((item) => item.truck_enquiry_id === enquiryId && item.transporter_id === transporterId);

    const toggleEnquirySelection = (e, item, topTransporter) => {
        if (!topTransporter) return;
        changeHandler(e, item, topTransporter);
    };

    const getDefaultTopTransporter = (item) => {
        const displayTransporters = item.transporter_list || item.transporter_rate_list || [];
        const rankedList = [...displayTransporters].sort((a, b) => {
            const rA = Number(String(getLastRate(a.rate_list)?.rate || '').replace(/,/g, '')) || Infinity;
            const rB = Number(String(getLastRate(b.rate_list)?.rate || '').replace(/,/g, '')) || Infinity;
            return rA - rB;
        });
        const nonRejectedRanked = rankedList.filter((t) => {
            const lastRate = getLastRate(t.rate_list);
            return lastRate?.bid_status === 'accepted' || lastRate?.bid_status === 'approved';
        });
        return nonRejectedRanked[0];
    };

    const selectableEnquiryIds = approvalList
        .filter((item) => !!getDefaultTopTransporter(item))
        .map((item) => item.truck_enquiry_id);

    const isAllSelected =
        selectableEnquiryIds.length > 0 &&
        selectableEnquiryIds.every((id) => isEnquirySelected(id));

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const newPayload = approvalList
                .map((item) => {
                    const explicitlySelected = selectedPayload.find(
                        (sel) => sel.truck_enquiry_id === item.truck_enquiry_id
                    );
                    const displayTransporters = item.transporter_list || item.transporter_rate_list || [];
                    const topTransporter = explicitlySelected
                        ? displayTransporters.find((t) => t.transporter_id === explicitlySelected.transporter_id) || getDefaultTopTransporter(item)
                        : getDefaultTopTransporter(item);
                    if (!topTransporter) return null;
                    return {
                        truck_enquiry_id: item.truck_enquiry_id,
                        transporter_id: topTransporter.transporter_id,
                        remark: explicitlySelected?.remark || '',
                    };
                })
                .filter(Boolean);
            setPayload(newPayload);
        } else {
            setPayload([]);
        }
    };

    const isPendingForUser = (item) => {
        return Array.isArray(item.approval_metrics)
            ? item.approval_metrics.some((m) => m.email === currentUserEmail && m.status === 'pending')
            : false;
    };

    const isDisabled = (enquiryId, transporterId) => {
        return !selectedPayload.some(
            (item) =>
                item.truck_enquiry_id === enquiryId &&
                item.transporter_id === transporterId
        );
    };

    const getTenure = (tenure) => {
        if (!tenure) return 'NA';
        if (typeof tenure === 'string') return tenure;
        if (Array.isArray(tenure) && tenure.length > 0) {
            return tenure[0].label || tenure[0].value || 'NA';
        }
        if (typeof tenure === 'object') {
            return tenure.label || tenure.value || 'NA';
        }
        return 'NA';
    };

    const formatRate = (value) => {
        if (value === null || value === undefined || value === '') return 'NA';
        const numericValue = Number(String(value).replace(/,/g, ''));
        if (Number.isFinite(numericValue)) {
            return numericValue.toLocaleString('en-IN');
        }
        return String(value);
    };

    const showRejectButtons =
        (enquiryTransition === 'yes' || enquiryTransition === true) &&
        selectedTab !== 'expiredEnquiries';

    const selectedHasIndentCreated = selectedPayload.some((sel) => {
        const enquiry = approvalList.find((e) => e.truck_enquiry_id === sel.truck_enquiry_id);
        return enquiry?.indent_created === true;
    });

    const getPreviousApprover = (item, topTransporter) => {
        const metrics = item.approval_metrics || [];
        const prev = metrics
            .filter((m) => m.status === 'approved' && m.level < currentUserLevel)
            .sort((a, b) => b.level - a.level)[0];
        if (prev) return { name: prev.name || prev.email, date: prev.status_updated_at?.$date || null };
        const acceptedEntry = (topTransporter?.rate_list || []).findLast((r) => r.bid_status === 'accepted');
        if (acceptedEntry) return { name: acceptedEntry.accepted_by, date: acceptedEntry.accepted_at?.$date || null };
        return null;
    };

    const handleRowApprove = (item, transporter) => {
        if (!transporter) return;
        const remarkKey = `${item.truck_enquiry_id}-${transporter.transporter_id}`;
        ApprovalHandler('approve', [{
            truck_enquiry_id: item.truck_enquiry_id,
            transporter_id: transporter.transporter_id,
            remark: rowRemarks[remarkKey] || '',
        }]);
    };

    const handleBulkApprove = () => {
        const payloadToApprove = selectedPayload.map((sel) => {
            const remarkKey = `${sel.truck_enquiry_id}-${sel.transporter_id}`;
            return {
                truck_enquiry_id: sel.truck_enquiry_id,
                transporter_id: sel.transporter_id,
                remark: rowRemarks[remarkKey] || '',
            };
        });
        ApprovalHandler('approve', payloadToApprove);
    };

    const getBidStatusBadge = (lastRate, truckRequired) => {
        const status = lastRate?.bid_status;
        if (status === 'accepted') return (
            <span style={{ backgroundColor: '#e8f8f0', color: '#27ae60', border: '1px solid #a8e6c3', borderRadius: '4px', padding: '2px 7px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                ACCEPTED
            </span>
        );
        if (status === 'approved') return (
            <span style={{ backgroundColor: '#eef0fb', color: '#393185', border: '1px solid #cfcbf0', borderRadius: '4px', padding: '2px 7px', fontSize: '11px', fontWeight: 700 }}>APPROVED</span>
        );
        if (status === 'rejected') return (
            <span style={{ backgroundColor: '#fdf0f0', color: '#e74c3c', border: '1px solid #f5c6c6', borderRadius: '4px', padding: '2px 7px', fontSize: '11px', fontWeight: 700 }}>REJECTED</span>
        );
        return (
            <span style={{ backgroundColor: '#f5f5f5', color: '#888', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '2px 7px', fontSize: '11px', fontWeight: 600 }}>QUOTED</span>
        );
    };

    /* ─── Column widths adjusted to align perfectly with center alignment ─── */
    const COL = {
        check: '3%',
        expand: '2%',
        adhoc: '10%',  // adhoc id + floater email + indent badge
        lane: '12%',
        truckType: '8%',
        numTruck: '4%',
        transporter: '10%',
        placement: '7%',   // date
        contractTenure: '8%',   // contract tenure column
        approvedRate: '8%',
        approvedBy: '10%',  // name + date + message count badge
        finalRemark: '10%',  // input + Send message
        // action: flex: 1 (occupies remainder)
    };

    const PANEL_COL = {
        rank: '7%',
        transporter: '24%',
        quote: '11%',
        status: '16%',
        acceptedBy: '20%',
        // reason: flex:1
    };

    const HEADER = { fontSize: '11px', fontWeight: 700, color: '#7b8299', letterSpacing: '0.4px' };

    return (
        <>
            {approvalList.length > 0 ? (
                <div>
                    <div className={tabStyles.scrollList}>
                        {/* ── Sticky table header (desktop) ── */}
                        {!mobile && (
                            <div className={tabStyles.tableHeader}>
                                <div style={{ width: COL.check, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        style={{ height: '15px', width: '15px', cursor: 'pointer' }}
                                    />
                                </div>
                                <div style={{ width: COL.expand, flexShrink: 0 }} />
                                <div style={{ width: COL.adhoc, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>ADHOC ID</div>
                                <div style={{ width: COL.lane, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>LANE</div>
                                <div style={{ width: COL.truckType, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>TRUCK TYPE</div>
                                <div style={{ width: COL.numTruck, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}># TRUCKS</div>
                                <div style={{ width: COL.transporter, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>TRANSPORTER</div>
                                <div style={{ width: COL.placement, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>PLACEMENT</div>
                                <div style={{ width: COL.contractTenure, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>CONTRACT TENURE</div>
                                <div style={{ width: COL.approvedRate, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>APPROVED RATE</div>
                                <div style={{ width: COL.approvedBy, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>RATE APPROVED BY</div>
                                <div style={{ width: COL.finalRemark, flexShrink: 0, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>FINAL REMARK</div>
                                <div style={{ flex: 1, ...HEADER, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>ACTION</div>
                            </div>
                        )}

                        {/* ── Enquiry rows ── */}
                        <div>
                            {approvalList.map((item, index) => {
                                const isPendingForCurrentUser = isPendingForUser(item);

                                const isPartyToApproval = Array.isArray(item.approval_metrics)
                                    ? item.approval_metrics.some((m) => m.email === currentUserEmail)
                                    : false;

                                const hasOpenClarification =
                                    item.clarification_status === 'clarification_needed' ||
                                    item.clarification_status === 'clarification_sent';

                                const userMetric = Array.isArray(item.approval_metrics)
                                    ? item.approval_metrics.find((m) => m.email === currentUserEmail)
                                    : null;

                                const showForClarificationOnly =
                                    !isPendingForCurrentUser && hasOpenClarification && isPartyToApproval;

                                const matchingTransporters = (item.transporter_list || item.transporter_rate_list || []).filter((transporter) => {
                                    const lastRate = getLastRate(transporter.rate_list);
                                    if (!lastRate) return false;
                                    const isFreshlyAccepted =
                                        lastRate.bid_status === 'accepted' &&
                                        (transporter.main_status === 'approval_pending' ||
                                            lastRate.main_status === 'approval_pending' ||
                                            (transporter.main_status !== 'approved' && lastRate.main_status !== 'approved'));
                                    const isPendingHigherLevelReview =
                                        isPendingForCurrentUser &&
                                        transporter.main_status !== 'rejected' &&
                                        lastRate.bid_status !== 'rejected';
                                    return isFreshlyAccepted || isPendingHigherLevelReview;
                                });

                                const displayTransporters = (item.transporter_list || item.transporter_rate_list || []);

                                const rankedList = [...displayTransporters].sort((a, b) => {
                                    const rA = Number(String(getLastRate(a.rate_list)?.rate || '').replace(/,/g, '')) || Infinity;
                                    const rB = Number(String(getLastRate(b.rate_list)?.rate || '').replace(/,/g, '')) || Infinity;
                                    return rA - rB;
                                });

                                const nonRejectedRanked = rankedList.filter((t) => {
                                    const lastRate = getLastRate(t.rate_list);
                                    return lastRate?.bid_status === 'accepted' || lastRate?.bid_status === 'approved';
                                });

                                const lane = item.lane_details || item;
                                const origin = lane.origin || item.origin;
                                const dest = lane.destination || item.destination;
                                const truckTypes = lane.truck_types || item.truck_types;
                                const truckRequired = lane.truck_required || item.truck_required;
                                const dueDate = lane.due_date || item.due_date;
                                const contractTenure = lane.contract_tenure || item.contract_tenure;

                                const originName = origin?.location?.location || origin?.location_name || origin?.label || 'NA';
                                const destName = dest?.location?.location || dest?.location_name || dest?.label || 'NA';
                                const truckLabel = Array.isArray(truckTypes)
                                    ? truckTypes.map((t) => t.label || t.value || t).join(', ')
                                    : truckTypes?.label || truckTypes?.value || truckTypes || 'NA';
                                const tenureLabel = getTenureLabel(contractTenure);

                                // Floater's display name (email before @)
                                const floaterEmail = item.email || '';
                                const floaterName = floaterEmail.split('@')[0] || '';

                                const adhocId = item.display_adhoc_id || 'NA';
                                const indentCreated = item.indent_created === true;
                                const totalQuoted = (item.transporter_list || item.transporter_rate_list || []).length;
                                const isExpanded = expandedRows.has(item.truck_enquiry_id);
                                const defaultTopTransporter = nonRejectedRanked[0];
                                const explicitlySelected = selectedPayload.find(
                                    (sel) => sel.truck_enquiry_id === item.truck_enquiry_id
                                );
                                const topTransporter = explicitlySelected
                                    ? displayTransporters.find((t) => t.transporter_id === explicitlySelected.transporter_id) || defaultTopTransporter
                                    : defaultTopTransporter;
                                const topTransporterIsAwarded = topTransporter?.is_awarded === true;
                                const indentLocksRow = indentCreated && !topTransporterIsAwarded;
                                const topTransporterRank = topTransporter
                                    ? rankedList.findIndex((t) => t.transporter_id === topTransporter.transporter_id) + 1
                                    : 0;
                                const topLastRate = getLastRate(topTransporter?.rate_list);
                                const topRate = formatRate(topLastRate?.rate);
                                const previousApprover = getPreviousApprover(item, topTransporter);
                                const remarkKey = `${item.truck_enquiry_id}-${topTransporter?.transporter_id}`;
                                const rowRemark = rowRemarks[remarkKey] || '';
                                const needsMandatoryRemark = isMandatoryApprovalRemark && topTransporterRank > 1 && rowRemark.trim() === '';
                                const historyEvents = buildHistory(item);
                                const approvalChainLevelsRaw = groupApprovalMetricsByLevel(item.approval_metrics);
                                const acceptedRateEntry = (topTransporter?.rate_list || []).find((r) => r.bid_status === 'accepted');
                                const acceptorLevel = acceptedRateEntry
                                    ? {
                                        level: 0,
                                        approvers: [{
                                            name: acceptedRateEntry.accepted_by,
                                            email: acceptedRateEntry.accepted_by_email,
                                            status: 'approved',
                                            transporter_name: topTransporter?.transporter_name,
                                        }],
                                    }
                                    : null;
                                const approvalChainLevels = acceptorLevel
                                    ? [acceptorLevel, ...approvalChainLevelsRaw]
                                    : approvalChainLevelsRaw;
                                const msgCount = item.no_of_msgs_received || 0;

                                // Calculate difference over L1 if top rate is higher than absolute lowest quote
                                const ratesArray = (item.transporter_list || item.transporter_rate_list || [])
                                    .map(t => getLastRate(t.rate_list)?.rate)
                                    .filter(r => r !== undefined && r !== null && r !== '');
                                const absoluteL1Rate = ratesArray.length > 0 ? Math.min(...ratesArray) : null;
                                const currentRateVal = topLastRate?.rate || null;
                                const diffOverL1 = (absoluteL1Rate !== null && currentRateVal !== null && currentRateVal > absoluteL1Rate)
                                    ? (currentRateVal - absoluteL1Rate)
                                    : null;

                                /* ── Mobile ── */
                                if (mobile) {
                                    return (
                                        <div key={`enquiry-${item.truck_enquiry_id || index}`}
                                            style={{ padding: '4%', margin: '2% 1%', border: '1px solid lightgrey', borderRadius: '5px', backgroundColor: '#fff' }}>
                                            <div style={{ cursor: 'pointer' }} onClick={() => toggleExpand(item.truck_enquiry_id, item)}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <input type="checkbox" checked={isEnquirySelected(item.truck_enquiry_id)}
                                                            disabled={showForClarificationOnly || indentLocksRow}
                                                            style={{ height: '16px', width: '16px' }}
                                                            className={tabStyles.checkbox}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => toggleEnquirySelection(e, item, topTransporter)} />
                                                        <b style={{ fontSize: '13px' }}>{adhocId}</b>
                                                    </div>
                                                    <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: '12px', color: '#393185' }} />
                                                </div>
                                                {floaterName && <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Raised by: {floaterName}</div>}
                                                {indentCreated && (
                                                    <span style={{ display: 'inline-block', marginTop: '4px', fontSize: '10px', fontWeight: 600, color: '#393185', backgroundColor: '#ECEBFA', border: '1px solid #cfcbf0', borderRadius: '10px', padding: '1px 7px' }}>
                                                        Indent Created
                                                    </span>
                                                )}
                                                {hasOpenClarification && (
                                                    <span
                                                        title={item.clarification_status === 'clarification_needed' ? 'Clarification requested' : 'Clarification sent'}
                                                        style={{ display: 'inline-block', marginTop: '4px', marginLeft: '4px', fontSize: '10px', fontWeight: 700, color: '#e67e22', backgroundColor: '#fff5ec', border: '1px solid #ffd3a8', borderRadius: '10px', padding: '1px 7px' }}>
                                                        Clarification
                                                    </span>
                                                )}
                                                {showForClarificationOnly
                                                    ? <div style={{ fontSize: '11px', color: '#e67e22', marginTop: '4px', fontWeight: 600 }}>Clarification requested</div>
                                                    : <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>{totalQuoted} quote{totalQuoted !== 1 ? 's' : ''} received, {matchingTransporters.length} accepted</div>}
                                                <div style={{ marginTop: '4px', fontSize: '13px' }}><b>Lane:</b> {originName} → {destName}</div>
                                                <div style={{ marginTop: '2px', fontSize: '12px' }}><b>Truck:</b> {truckLabel}{tenureLabel ? ` · {tenureLabel}` : ''}</div>
                                                <div style={{ marginTop: '2px', fontSize: '13px' }}><b>Transporter:</b> {topTransporter?.transporter_name || 'NA'}</div>
                                                <div style={{ marginTop: '4px', fontWeight: 700, color: '#393185', fontSize: '15px' }}>
                                                    {topRate === 'NA' ? 'NA' : `₹${topRate}`}
                                                    {topTransporter && <span style={{ fontSize: '12px', color: '#888', marginLeft: '6px' }}>L{topTransporterRank}</span>}
                                                </div>
                                                {diffOverL1 !== null && (
                                                    <div style={{ fontSize: '11px', color: '#e67e22', fontWeight: 700, marginTop: '2px' }}>
                                                        ₹{formatRate(diffOverL1)} over L1
                                                    </div>
                                                )}
                                            </div>
                                            {!showForClarificationOnly && (
                                                <div style={{ marginTop: '8px' }}>
                                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                        <input type="text" placeholder={needsMandatoryRemark ? 'Remark is mandatory*' : 'Add final remark...'} value={rowRemark}
                                                            onChange={(e) => setRowRemarks((prev) => ({ ...prev, [remarkKey]: e.target.value }))}
                                                            className="form-control form-control-sm"
                                                            style={{
                                                                flex: 1, fontSize: '12px', height: '30px',
                                                                backgroundColor: needsMandatoryRemark ? '#fdecea' : '#fff',
                                                                border: needsMandatoryRemark ? '1px solid #e74c3c' : undefined,
                                                            }} />
                                                        {indentLocksRow ? (
                                                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#7b8299', whiteSpace: 'nowrap' }}>
                                                                <i className="fa fa-lock" /> Indent created
                                                            </span>
                                                        ) : (
                                                            <button className="btn btn-sm btn-primary" onClick={() => handleRowApprove(item, topTransporter)} disabled={!topTransporter}>Approve</button>
                                                        )}
                                                    </div>
                                                    {needsMandatoryRemark && (
                                                        <span style={{ fontSize: '10px', color: '#e74c3c', fontWeight: 600 }}>
                                                            Remark required
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            <div style={{ marginTop: '6px' }}>
                                                <MessageModalButton
                                                    enquiryId={item.truck_enquiry_id}
                                                    transporters={item.transporter_list || item.transporter_rate_list || []}
                                                    currentUserLevel={currentUserLevel}
                                                    currentUserEmail={currentUserEmail}
                                                    currentUserName={currentUserName}
                                                    clarificationStatus={item.clarification_status}
                                                    initialUnreadCount={msgCount}
                                                />
                                            </div>
                                        </div>
                                    );
                                }

                                /* ── Desktop ── */
                                const isRowSelected = isEnquirySelected(item.truck_enquiry_id);
                                return (
                                    <div key={`enquiry-${item.truck_enquiry_id || index}`}>
                                        {/* ── Summary row ── */}
                                        <div
                                            style={{
                                                display: 'flex', alignItems: 'center',
                                                padding: '12px 0', cursor: 'pointer',
                                                backgroundColor: isRowSelected ? '#ECEBFA' : (isExpanded ? '#f8f9ff' : 'transparent'),
                                                borderBottom: '1px solid #edf0f7',
                                                transition: 'background-color 0.2s ease',
                                            }}
                                            onClick={() => toggleExpand(item.truck_enquiry_id, item)}
                                        >
                                            {/* Checkbox */}
                                            <div style={{ width: COL.check, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                <input type="checkbox" checked={isRowSelected}
                                                    disabled={showForClarificationOnly || userMetric?.status === 'approved' || indentLocksRow}
                                                    style={{ height: '15px', width: '15px', cursor: (showForClarificationOnly || userMetric?.status === 'approved' || indentLocksRow) ? 'not-allowed' : 'pointer' }}
                                                    className={tabStyles.checkbox}
                                                    onChange={(e) => toggleEnquirySelection(e, item, topTransporter)} />
                                            </div>

                                            {/* Expand arrow */}
                                            <div style={{ width: COL.expand, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                                                <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: '11px', color: '#393185' }} />
                                                {totalQuoted > 1 && (
                                                    <span
                                                        title={`${totalQuoted} transporters quoted`}
                                                        style={{ fontSize: '9px', color: '#7b8299', fontWeight: 700, whiteSpace: 'nowrap' }}
                                                    >
                                                        +{totalQuoted - 1}
                                                    </span>
                                                )}
                                            </div>

                                            {/* ADHOC ID */}
                                            <div style={{ width: COL.adhoc, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 4px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#2d3250', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {adhocId}
                                                </div>
                                                {floaterName && (
                                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        Raised by: {floaterName}
                                                    </div>
                                                )}
                                                {showForClarificationOnly ? (
                                                    <div style={{ fontSize: '11px', color: '#e67e22', marginTop: '2px', fontWeight: 600 }}>
                                                        Clarification requested
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                                        {matchingTransporters.length} of {totalQuoted} quotes accepted
                                                    </div>
                                                )}
                                                {indentCreated && (
                                                    <span style={{
                                                        display: 'inline-block', marginTop: '3px', fontSize: '10px', fontWeight: 700,
                                                        color: '#393185', backgroundColor: '#ECEBFA', border: '1px solid #cfcbf0',
                                                        borderRadius: '10px', padding: '1px 7px', whiteSpace: 'nowrap',
                                                    }}>
                                                        INDENT CREATED
                                                    </span>
                                                )}
                                                {hasOpenClarification && (
                                                    <span
                                                        title={item.clarification_status === 'clarification_needed' ? 'Clarification requested' : 'Clarification sent'}
                                                        style={{
                                                            display: 'inline-block', marginTop: '3px', fontSize: '10px', fontWeight: 700,
                                                            color: '#e67e22', backgroundColor: '#fff5ec', border: '1px solid #ffd3a8',
                                                            borderRadius: '10px', padding: '1px 7px', whiteSpace: 'nowrap',
                                                        }}>
                                                        CLARIFICATION
                                                    </span>
                                                )}
                                            </div>

                                            {/* Lane */}
                                            <div style={{ width: COL.lane, flexShrink: 0, padding: '0 4px' }}>
                                                <div className={tabStyles.laneCell}>
                                                    <span className={tabStyles.laneLocation} title={originName}>{originName}</span>
                                                    <i className={`fa fa-long-arrow-right ${tabStyles.laneArrow}`} />
                                                    <span className={tabStyles.laneLocation} title={destName}>{destName}</span>
                                                </div>
                                            </div>

                                            {/* Truck Type */}
                                            <div style={{ width: COL.truckType, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: '12px', color: '#555', whiteSpace: 'normal', wordBreak: 'break-word', padding: '0 4px' }}>
                                                {truckLabel}
                                            </div>

                                            {/* # Trucks */}
                                            <div style={{ width: COL.numTruck, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: '13px', color: '#555' }}>
                                                {truckRequired || 'NA'}
                                            </div>

                                            {/* Transporter (L1) */}
                                            <div style={{ width: COL.transporter, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 4px' }}>
                                                <div style={{ fontSize: '13px', color: '#333', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                    {topTransporter?.transporter_name || 'NA'}
                                                </div>
                                                {topTransporter && (
                                                    <span style={{ fontSize: '10px', color: '#e67e22', fontWeight: 700, backgroundColor: '#fff5ec', border: '1px solid #ffd3a8', borderRadius: '10px', padding: '1px 6px', marginTop: '2px', whiteSpace: 'nowrap' }}>
                                                        L1
                                                    </span>
                                                )}
                                            </div>

                                            {/* Placement */}
                                            <div style={{ width: COL.placement, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: '12px', color: '#555' }}>
                                                {dueDate || 'NA'}
                                            </div>

                                            {/* Contract Tenure */}
                                            <div style={{ width: COL.contractTenure, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: '12px', color: '#555', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                {tenureLabel || '—'}
                                            </div>

                                            {/* Approved Rate */}
                                            <div style={{ width: COL.approvedRate, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                                <div style={{ fontWeight: 700, color: '#393185', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                                    {topRate === 'NA'
                                                        ? <span style={{ color: '#bbb', fontWeight: 400, fontSize: '12px' }}>NA</span>
                                                        : `₹${topRate}`}
                                                </div>
                                                {topTransporter && topRate !== 'NA' && (
                                                    <span style={{ fontSize: '10px', color: '#888' }}>L{topTransporterRank}</span>
                                                )}
                                                {diffOverL1 !== null && (
                                                    <div style={{ fontSize: '10px', color: '#e67e22', fontWeight: 700, marginTop: '2px', whiteSpace: 'nowrap' }}>
                                                        ₹{formatRate(diffOverL1)} over L1
                                                    </div>
                                                )}
                                            </div>

                                            {/* Rate Approved By (+ message count badge) */}
                                            <div style={{ width: COL.approvedBy, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 4px' }}>
                                                {previousApprover ? (
                                                    <>
                                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#333', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {previousApprover.name}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '1px', whiteSpace: 'nowrap' }}>
                                                            {formatShortDate(previousApprover.date)}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span style={{ fontSize: '12px', color: '#bbb' }}>—</span>
                                                )}
                                                {msgCount > 0 && (
                                                    <div style={{ marginTop: '4px' }}>
                                                        <span style={{
                                                            fontSize: '10px', fontWeight: 700, color: '#393185',
                                                            backgroundColor: '#eef0fb', border: '1px solid #cfcbf0',
                                                            borderRadius: '10px', padding: '1px 7px', whiteSpace: 'nowrap',
                                                        }}>
                                                            {msgCount} MESSAGE{msgCount > 1 ? 'S' : ''}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Final Remark (Remark Input & Send Message Link) */}
                                            <div style={{ width: COL.finalRemark, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                onClick={(e) => e.stopPropagation()}>
                                                {!showForClarificationOnly ? (
                                                    <>
                                                        <input
                                                            type="text"
                                                            placeholder={needsMandatoryRemark ? 'Remark is mandatory*' : 'Add final remark...'}
                                                            value={rowRemark}
                                                            disabled={userMetric?.status === 'approved'}
                                                            onChange={(e) => setRowRemarks((prev) => ({ ...prev, [remarkKey]: e.target.value }))}
                                                            className={tabStyles.remarkInput}
                                                            style={{
                                                                width: '100%',
                                                                maxWidth: '140px',
                                                                fontSize: '12px',
                                                                height: '32px',
                                                                textAlign: 'center',
                                                                cursor: userMetric?.status === 'approved' ? 'not-allowed' : 'text',
                                                                backgroundColor: userMetric?.status === 'approved' ? '#f5f6f8' : (needsMandatoryRemark ? '#fdecea' : '#fff'),
                                                                border: needsMandatoryRemark ? '1px solid #e74c3c' : undefined,
                                                            }}
                                                        />
                                                        {needsMandatoryRemark && (
                                                            <span style={{ fontSize: '10px', color: '#e74c3c', fontWeight: 600 }}>
                                                                Remark required
                                                            </span>
                                                        )}
                                                        <MessageModalButton
                                                            enquiryId={item.truck_enquiry_id}
                                                            transporters={item.transporter_list || item.transporter_rate_list || []}
                                                            currentUserLevel={currentUserLevel}
                                                            currentUserEmail={currentUserEmail}
                                                            currentUserName={currentUserName}
                                                            clarificationStatus={item.clarification_status}
                                                            initialUnreadCount={msgCount}
                                                        />
                                                    </>
                                                ) : (
                                                    <MessageModalButton
                                                        enquiryId={item.truck_enquiry_id}
                                                        transporters={item.transporter_list || item.transporter_rate_list || []}
                                                        currentUserLevel={currentUserLevel}
                                                        currentUserEmail={currentUserEmail}
                                                        currentUserName={currentUserName}
                                                        clarificationStatus={item.clarification_status}
                                                        initialUnreadCount={msgCount}
                                                    />
                                                )}
                                            </div>

                                            {/* Action Column (Approve) */}
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                onClick={(e) => e.stopPropagation()}>
                                                {userMetric?.status === 'approved' ? (
                                                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#27ae60', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                        <i className="fa fa-check-circle" /> Approved
                                                    </span>
                                                ) : indentLocksRow ? (
                                                    <span
                                                        style={{ fontSize: '12px', fontWeight: 600, color: '#7b8299', display: 'inline-flex', alignItems: 'center', gap: '4px', textAlign: 'center' }}
                                                        title="An indent has already been created for this enquiry by another approver"
                                                    >
                                                        <i className="fa fa-lock" /> Indent already created
                                                    </span>
                                                ) : (
                                                    <button
                                                        style={{
                                                            whiteSpace: 'nowrap',
                                                            padding: '6px 14px',
                                                            border: 'none',
                                                            color: '#fff',
                                                            backgroundColor: '#393185',
                                                            fontWeight: 600,
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '13px',
                                                            transition: 'all 0.15s ease-in-out',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                        }}
                                                        onMouseEnter={(e) => { e.target.style.backgroundColor = '#2c256d'; }}
                                                        onMouseLeave={(e) => { e.target.style.backgroundColor = '#393185'; }}
                                                        onClick={() => handleRowApprove(item, topTransporter)}
                                                        disabled={!topTransporter}>
                                                        Approve
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* ── Expanded panel: QUOTES RECEIVED + HISTORY ── */}
                                        {isExpanded && (
                                            <div style={{ backgroundColor: '#f7f8fc', padding: '16px 20px 20px', marginBottom: '4px', borderBottom: '2px solid #e4e8f0' }}>

                                                {/* QUOTES RECEIVED */}
                                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#7b8299', letterSpacing: '0.5px', marginBottom: '8px' }}>
                                                    QUOTES RECEIVED
                                                </div>
                                                <div style={{ display: 'flex', padding: '6px 0', fontSize: '11px', fontWeight: 700, color: '#555', borderBottom: '1px solid #e0e2ec', marginBottom: '2px' }}>
                                                    <div style={{ width: PANEL_COL.rank }}>RANK</div>
                                                    <div style={{ width: PANEL_COL.transporter }}>TRANSPORTER</div>
                                                    <div style={{ width: PANEL_COL.quote }}>QUOTE</div>
                                                    <div style={{ width: PANEL_COL.status }}>STATUS</div>
                                                    <div style={{ width: PANEL_COL.acceptedBy }}>ACCEPTED BY</div>
                                                    <div style={{ flex: 1 }}>REASON FOR ACCEPTANCE</div>
                                                </div>

                                                {rankedList.map((transporter, i) => {
                                                    const lastRate = getLastRate(transporter.rate_list);
                                                    const rate = formatRate(lastRate?.rate);
                                                    const isSelected = isTransporterSelected(item.truck_enquiry_id, transporter.transporter_id);
                                                    const acceptedEntry = (transporter.rate_list || []).findLast((r) => r.bid_status === 'accepted');
                                                    // Once a transporter's quote has been approved or rejected, it's a
                                                    // terminal decision — don't let it be (re-)selected for approval.
                                                    const isDecided = lastRate?.bid_status === 'approved' || lastRate?.bid_status === 'rejected';
                                                    const isAccepted = lastRate?.bid_status === 'accepted';
                                                    const rowDisabled = showForClarificationOnly || isDecided || !isAccepted;
                                                    return (
                                                        <div key={`d-${item.truck_enquiry_id}-${transporter.transporter_id}`}
                                                            style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderTop: i > 0 ? '1px dashed #e0e2ec' : 'none', fontSize: '13px' }}>
                                                            <div style={{ width: PANEL_COL.rank, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <input type="checkbox" checked={isSelected} disabled={rowDisabled}
                                                                    style={{ height: '14px', width: '14px', cursor: rowDisabled ? 'not-allowed' : 'pointer' }}
                                                                    className={tabStyles.checkbox}
                                                                    onChange={(e) => changeHandler(e, item, transporter)} />
                                                                <span style={{ color: '#e67e22', fontWeight: 700, fontSize: '12px' }}>L{i + 1}</span>
                                                            </div>
                                                            <div style={{ width: PANEL_COL.transporter, paddingRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {transporter.transporter_name || 'NA'}
                                                                {indentCreated && transporter.is_awarded === true && (
                                                                    <span style={{
                                                                        display: 'inline-block', marginLeft: '6px', fontSize: '9px', fontWeight: 700,
                                                                        color: '#393185', backgroundColor: '#ECEBFA', border: '1px solid #cfcbf0',
                                                                        borderRadius: '9px', padding: '1px 6px', whiteSpace: 'nowrap', verticalAlign: 'middle',
                                                                    }}>
                                                                        Indent Created
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{ width: PANEL_COL.quote, fontWeight: 700, color: '#393185' }}>
                                                                {rate === 'NA' ? 'NA' : `₹${rate}`}
                                                            </div>
                                                            <div style={{ width: PANEL_COL.status }}>
                                                                {getBidStatusBadge(lastRate, item.truck_required)}
                                                            </div>
                                                            <div style={{ width: PANEL_COL.acceptedBy, paddingRight: '6px' }}>
                                                                {acceptedEntry ? (
                                                                    <>
                                                                        <div style={{ fontWeight: 600, color: '#333' }}>{acceptedEntry.accepted_by || '—'}</div>
                                                                        <div style={{ fontSize: '11px', color: '#888' }}>{formatShortDate(acceptedEntry.accepted_at?.$date)}</div>
                                                                    </>
                                                                ) : <span style={{ color: '#aaa' }}>—</span>}
                                                            </div>
                                                            <div style={{ flex: 1, fontSize: '12px', color: '#555', fontStyle: acceptedEntry?.remark ? 'normal' : 'italic' }}>
                                                                {acceptedEntry?.remark || <span style={{ color: '#bbb' }}>—</span>}
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {/* APPROVAL CHAIN */}
                                                {approvalChainLevels.length > 0 && (
                                                    <div className={tabStyles.approvalChainSection}>
                                                        <button
                                                            type="button"
                                                            className={tabStyles.approvalChainToggle}
                                                            onClick={() => toggleApprovalChain(item.truck_enquiry_id)}
                                                        >
                                                            <i className={`fa fa-chevron-${expandedApprovalChains.has(item.truck_enquiry_id) ? 'down' : 'right'}`} />
                                                            APPROVAL CHAIN
                                                        </button>
                                                        {expandedApprovalChains.has(item.truck_enquiry_id) && (
                                                            <div className={tabStyles.approvalChainLevels}>
                                                                {approvalChainLevels.map(({ level, approvers }) => (
                                                                    <div key={`level-${level}`} className={tabStyles.approvalChainLevelGroup}>
                                                                        <span className={tabStyles.approvalChainLevelLabel}>L{level}</span>
                                                                        <div className={tabStyles.approvalChainNames}>
                                                                            {approvers.map((approver, ai) => {
                                                                                const displayName = approver.name || approver.email || 'Unknown';
                                                                                const initial = displayName.trim().charAt(0).toUpperCase() || '?';
                                                                                return (
                                                                                    <div key={`${level}-${approver.email || ai}`} className={tabStyles.approvalChainNameRow}>
                                                                                        <span className={tabStyles.approvalChainAvatar}>{initial}</span>
                                                                                        <div>
                                                                                            <div className={tabStyles.approvalChainName}>{displayName}</div>
                                                                                            {approver.status === 'approved' && approver.transporter_name && (
                                                                                                <div className={tabStyles.approvalChainAccepted}>
                                                                                                    {level === 0 ? 'Accepted' : 'Approved'}: {approver.transporter_name}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* HISTORY */}
                                                {historyEvents.length > 0 && (
                                                    <div className={historyStyles.section}>
                                                        <div className={historyStyles.sectionLabel}>HISTORY</div>
                                                        <div className={historyStyles.timeline}>
                                                            {historyEvents.map((h, hi) => (
                                                                <React.Fragment key={`hist-${hi}`}>
                                                                    {hi > 0 && <div className={historyStyles.connector} />}
                                                                    <div className={historyStyles.item}>
                                                                        <div className={historyStyles.card} style={{ borderTopColor: h.color }}>
                                                                            <div className={historyStyles.stepHeader}>
                                                                                <span className={historyStyles.stepNumber} style={{ backgroundColor: h.color }}>
                                                                                    {hi + 1}
                                                                                </span>
                                                                                <span className={historyStyles.timestamp}>{formatShortDate(h.ts)}</span>
                                                                            </div>
                                                                            <div className={historyStyles.text}>{h.text}</div>
                                                                            {h.remark && (
                                                                                <div className={historyStyles.remark}>"{h.remark}"</div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </React.Fragment>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── Bottom: reject buttons for checkbox-selected rows ── */}
                    <center className="pt-4">
                        {showRejectButtons && (
                            <>
                                <button type="button"
                                    className="btn btn-sm btn-outline-danger mx-2"
                                    disabled={selectedHasIndentCreated}
                                    style={{
                                        border: '1px solid #dc3545',
                                        borderRadius: '4px',
                                        padding: '6px 18px',
                                        fontWeight: 600,
                                        backgroundColor: '#fff',
                                        color: '#dc3545',
                                        opacity: selectedHasIndentCreated ? 0.5 : 1,
                                        cursor: selectedHasIndentCreated ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.15s ease-in-out',
                                        fontSize: '13px'
                                    }}
                                    onMouseEnter={(e) => { if (!selectedHasIndentCreated) e.target.style.backgroundColor = '#fdf0f0'; }}
                                    onMouseLeave={(e) => { if (!selectedHasIndentCreated) e.target.style.backgroundColor = '#fff'; }}
                                    onClick={() => ApprovalHandler('close')}>
                                    Reject and Close
                                </button>
                                <button type="button"
                                    className="btn btn-sm btn-outline-secondary mx-2"
                                    disabled={selectedHasIndentCreated}
                                    style={{
                                        border: '1px solid #6c757d',
                                        borderRadius: '4px',
                                        padding: '6px 18px',
                                        fontWeight: 600,
                                        backgroundColor: '#fff',
                                        color: '#6c757d',
                                        opacity: selectedHasIndentCreated ? 0.5 : 1,
                                        cursor: selectedHasIndentCreated ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.15s ease-in-out',
                                        fontSize: '13px'
                                    }}
                                    onMouseEnter={(e) => { if (!selectedHasIndentCreated) e.target.style.backgroundColor = '#f5f5f5'; }}
                                    onMouseLeave={(e) => { if (!selectedHasIndentCreated) e.target.style.backgroundColor = '#fff'; }}
                                    onClick={() => ApprovalHandler('open')}>
                                    Reject and Open
                                </button>
                            </>
                        )}
                        {selectedPayload.length > 0 && (
                            <button type="button"
                                className="btn btn-sm btn-success mx-2"
                                style={{
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '6px 18px',
                                    fontWeight: 600,
                                    backgroundColor: '#27ae60',
                                    color: '#fff',
                                    transition: 'all 0.15s ease-in-out',
                                    fontSize: '13px',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => { e.target.style.backgroundColor = '#219653'; }}
                                onMouseLeave={(e) => { e.target.style.backgroundColor = '#27ae60'; }}
                                onClick={handleBulkApprove}>
                                Approve
                            </button>
                        )}
                    </center>

                    {selectedHasIndentCreated && showRejectButtons && (
                        <div style={{ textAlign: 'center', fontSize: '11px', color: '#e67e22', marginTop: '4px' }}>
                            Enquiries with an indent already created cannot be Rejected — only Approve is allowed for them.
                        </div>
                    )}

                    <center className="pt-2">
                        <Pagination
                            activePage={skipActive === 0 ? 1 : skipActive}
                            itemsCountPerPage={10}
                            totalItemsCount={totalCount}
                            onChange={(e) => getOnChange(e)}
                            itemClass="page-item"
                            linkClass="page-link"
                        />
                    </center>

                    <div className="d-flex justify-content-end mt-2 pb-3" style={{ fontSize: '12px', color: '#666' }}>
                        <b>Note:&nbsp;</b> If the Date of Placement is expired then new enquiry will be floated
                    </div>
                </div>
            ) : (
                show === false && (
                    <div className="mb-5 pb-5">
                        <center>
                                <DataGhost title="Looks like you have no enquiries to approve" hasSubtitle={false} />
                        </center>
                    </div>
                )
            )}
        </>
    );
};

export default EnquiriesTab;